import { Badge, Button, cn } from '@newfront-insurance/core-ui/v2';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, Clock3, X } from 'lucide-react';
import { useState, useSyncExternalStore } from 'react';

import { NovaScheduleAccountEditor } from './nova-schedule-account-editor';
import { NovaScheduleEditorFooter } from './nova-schedule-editor-footer';
import {
  NovaScheduleDeliveryEditor,
  NovaScheduleProposalFields,
  NovaScheduleWhenEditor,
} from './nova-schedule-proposal-fields';
import { ToolHandoffCard, ToolHandoffCardFooter, ToolHandoffCardSurface } from './primitives/tool-handoff-card';
import type { FileAttachment } from '../../../../hooks/use-file-selection';
import {
  buildNovaScheduleProposalDraft,
  formatNovaScheduleDateTime,
  formatNovaScheduleRecurrence,
  getBrowserTimezone,
  hasNovaScheduleTimingChanged,
  NO_ACCOUNT_SELECTED_SCHEDULE_LABEL,
  validateNovaScheduleProposalDraft,
  type NovaScheduleProposalDraft,
  type ResolvedNovaScheduleProposalOutput,
  type StoredNovaScheduleProposal,
} from '../../../../lib/nova-schedule-proposal';
import type { SelectedAccount } from '../../../account-selector';
import { novaGradientClass } from '../../design-system/gradients';
import {
  NovaPromptEditor,
  buildNovaPromptFiles,
  hasUnsettledNovaPromptAttachments,
  mergeNovaPromptFiles,
  type NovaPromptFile,
} from '../chat-input/nova-prompt-editor';

interface PendingNovaScheduleProposalCardProps {
  state: 'pending';
  proposal: StoredNovaScheduleProposal;
  pendingAction?: 'confirm' | 'cancel';
  error?: string;
  disabled?: boolean;
  previewPending?: boolean;
  initialAccount?: SelectedAccount;
  showAccount?: boolean;
  canSelectAccount?: boolean;
  userUuid?: string;
  userScopes?: string[];
  initialAttachmentFiles?: NovaPromptFile[];
  onCancel: () => void;
  onConfirm: (draft: NovaScheduleProposalDraft, account: SelectedAccount) => void;
}

interface ResolvedNovaScheduleProposalCardProps {
  state: 'resolved';
  proposal: StoredNovaScheduleProposal;
  output: ResolvedNovaScheduleProposalOutput;
  onManageSchedules?: () => void;
}

export type NovaScheduleProposalCardProps =
  | PendingNovaScheduleProposalCardProps
  | ResolvedNovaScheduleProposalCardProps;

const subscribeToStaticBrowserTimezone = () => () => undefined;
const VIEW_TRANSITION = { duration: 0.2, ease: [0.22, 1, 0.36, 1] as const };
type PendingScheduleView = 'summary' | 'account' | 'when' | 'delivery' | 'prompt';

function getScheduleViewMotionProps(direction: 1 | -1, reducedMotion: boolean) {
  if (reducedMotion) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.12, ease: 'linear' as const },
    };
  }

  const offset = 24;

  return {
    initial: { opacity: 0, x: direction > 0 ? offset : -offset },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: direction > 0 ? -offset : offset },
    transition: VIEW_TRANSITION,
  };
}

export function NovaScheduleProposalCard(props: NovaScheduleProposalCardProps) {
  const browserTimezone = useSyncExternalStore(
    subscribeToStaticBrowserTimezone,
    () => getBrowserTimezone(props.proposal.timezone),
    () => props.proposal.timezone,
  );

  return (
    <div className="my-3 w-full max-w-[727px]">
      <ToolHandoffCard className="p-[2px]">
        <ToolHandoffCardSurface
          layout
          className="overflow-hidden rounded-[14px]"
          transition={{ layout: VIEW_TRANSITION }}
        >
          {props.state === 'pending' ? (
            <PendingNovaScheduleProposalCard {...props} browserTimezone={browserTimezone} />
          ) : (
            <ResolvedNovaScheduleProposalCard {...props} browserTimezone={browserTimezone} />
          )}
        </ToolHandoffCardSurface>
      </ToolHandoffCard>
    </div>
  );
}

function PendingNovaScheduleProposalCard({
  proposal,
  pendingAction,
  error,
  disabled = false,
  previewPending = false,
  initialAccount,
  showAccount = true,
  canSelectAccount = false,
  userUuid,
  userScopes,
  initialAttachmentFiles = [],
  onCancel,
  onConfirm,
  browserTimezone,
}: PendingNovaScheduleProposalCardProps & { browserTimezone: string }) {
  const [draft, setDraft] = useState<NovaScheduleProposalDraft>(() => buildNovaScheduleProposalDraft(proposal));
  const [editorDraft, setEditorDraft] = useState<NovaScheduleProposalDraft>(() =>
    buildNovaScheduleProposalDraft(proposal),
  );
  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount>(initialAccount);
  const [editorAccount, setEditorAccount] = useState<SelectedAccount>(initialAccount);
  const [promptFiles, setPromptFiles] = useState<NovaPromptFile[]>(() =>
    buildNovaPromptFiles(proposal.attachmentFileUuids, initialAttachmentFiles),
  );
  const [editorPromptFiles, setEditorPromptFiles] = useState<NovaPromptFile[]>(promptFiles);
  const [editorPromptAttachments, setEditorPromptAttachments] = useState<FileAttachment[]>([]);
  const [validationError, setValidationError] = useState<string>();
  const [activeView, setActiveView] = useState<PendingScheduleView>('summary');
  const [viewDirection, setViewDirection] = useState<1 | -1>(1);
  const prefersReducedMotion = useReducedMotion();
  const isBusy = pendingAction !== undefined;
  const hasDraftTimingChanged = hasNovaScheduleTimingChanged(proposal, draft);
  const hasEditorTimingChanged = hasNovaScheduleTimingChanged(proposal, editorDraft);
  const firstRunAt = proposal.preview?.firstRunAt;
  const isFirstRunUnavailable = !firstRunAt && !previewPending;
  const firstRunLabel =
    !hasDraftTimingChanged && firstRunAt ? formatNovaScheduleDateTime(firstRunAt, browserTimezone) : undefined;
  const editorFirstRunLabel =
    !hasEditorTimingChanged && firstRunAt ? formatNovaScheduleDateTime(firstRunAt, browserTimezone) : undefined;

  const handleConfirm = () => {
    if (!firstRunAt) return;

    const draftForBrowserTimezone = { ...draft, timezone: browserTimezone };
    const nextValidationError = validateNovaScheduleProposalDraft(draftForBrowserTimezone);
    setValidationError(nextValidationError);
    if (nextValidationError) return;

    onConfirm(
      {
        ...draftForBrowserTimezone,
        title: draft.title.trim(),
        prompt: draft.prompt.trim(),
      },
      selectedAccount,
    );
  };

  const visibleError = error ?? validationError;
  const openEditor = (view: Exclude<PendingScheduleView, 'summary'>) => {
    setEditorDraft(draft);
    setEditorAccount(selectedAccount);
    if (view === 'prompt') {
      setEditorPromptFiles(promptFiles);
      setEditorPromptAttachments([]);
    }
    setViewDirection(1);
    setActiveView(view);
  };
  const returnToSummary = () => {
    setViewDirection(-1);
    setActiveView('summary');
  };
  const updateDraftFromEditor = () => {
    setDraft(editorDraft);
    setValidationError(undefined);
    returnToSummary();
  };
  const updateAccountFromEditor = () => {
    setSelectedAccount(editorAccount);
    returnToSummary();
  };
  const updatePromptFromEditor = () => {
    const nextPromptFiles = mergeNovaPromptFiles(editorPromptFiles, editorPromptAttachments);
    setPromptFiles(nextPromptFiles);
    setDraft({
      ...editorDraft,
      attachmentFileUuids: nextPromptFiles.map((file) => file.uuid),
    });
    setValidationError(undefined);
    setEditorPromptAttachments([]);
    returnToSummary();
  };

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={activeView}
        {...getScheduleViewMotionProps(viewDirection, Boolean(prefersReducedMotion))}
        className="flex w-full flex-col"
      >
        {activeView === 'summary' && (
          <PendingScheduleSummary
            draft={draft}
            firstRunLabel={firstRunLabel}
            firstRunLoading={!hasDraftTimingChanged && previewPending}
            firstRunUnavailable={isFirstRunUnavailable}
            firstRunAvailable={Boolean(firstRunAt)}
            disabled={disabled || isBusy}
            pendingAction={pendingAction}
            error={visibleError}
            accountLabel={selectedAccount?.label ?? NO_ACCOUNT_SELECTED_SCHEDULE_LABEL}
            showAccount={showAccount}
            canEditAccount={canSelectAccount && Boolean(userUuid && userScopes)}
            onEditPrompt={() => openEditor('prompt')}
            onEditAccount={() => openEditor('account')}
            onEditWhen={() => openEditor('when')}
            onEditDelivery={() => openEditor('delivery')}
            onCancel={onCancel}
            onConfirm={handleConfirm}
          />
        )}

        {activeView === 'account' && userUuid && userScopes && (
          <NovaScheduleAccountEditor
            selectedAccount={editorAccount}
            loggedInUserUuid={userUuid}
            userScopes={userScopes}
            disabled={disabled || isBusy}
            onChange={setEditorAccount}
            onBack={returnToSummary}
            onUpdate={updateAccountFromEditor}
          />
        )}

        {activeView === 'when' && (
          <ScheduleWhenPanel
            draft={editorDraft}
            firstRunLabel={editorFirstRunLabel}
            browserTimezone={browserTimezone}
            disabled={disabled || isBusy}
            onChange={setEditorDraft}
            onBack={returnToSummary}
            onUpdate={updateDraftFromEditor}
          />
        )}

        {activeView === 'delivery' && (
          <ScheduleDeliveryPanel
            draft={editorDraft}
            disabled={disabled || isBusy}
            onChange={setEditorDraft}
            onBack={returnToSummary}
            onUpdate={updateDraftFromEditor}
          />
        )}

        {activeView === 'prompt' && (
          <SchedulePromptPanel
            draft={editorDraft}
            accountUuid={editorAccount?.uuid}
            existingFiles={editorPromptFiles}
            attachments={editorPromptAttachments}
            disabled={disabled || isBusy}
            onChange={setEditorDraft}
            onExistingFilesChange={setEditorPromptFiles}
            setAttachments={setEditorPromptAttachments}
            onBack={returnToSummary}
            onUpdate={updatePromptFromEditor}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
}

function PendingScheduleSummary({
  draft,
  firstRunLabel,
  firstRunLoading,
  firstRunUnavailable,
  firstRunAvailable,
  disabled,
  pendingAction,
  error,
  accountLabel,
  showAccount,
  canEditAccount,
  onEditPrompt,
  onEditAccount,
  onEditWhen,
  onEditDelivery,
  onCancel,
  onConfirm,
}: {
  draft: NovaScheduleProposalDraft;
  firstRunLabel?: string;
  firstRunLoading: boolean;
  firstRunUnavailable: boolean;
  firstRunAvailable: boolean;
  disabled: boolean;
  pendingAction?: 'confirm' | 'cancel';
  error?: string;
  accountLabel: string;
  showAccount: boolean;
  canEditAccount: boolean;
  onEditPrompt: () => void;
  onEditAccount: () => void;
  onEditWhen: () => void;
  onEditDelivery: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="flex min-h-[368px] flex-col">
      <div className="flex items-start justify-between gap-4 px-[30px] pt-5 pb-4">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <p className="text-steel-500 text-[14px] leading-5 font-semibold">{draft.title}</p>
          <button
            type="button"
            aria-label="Edit schedule prompt"
            disabled={disabled}
            onClick={onEditPrompt}
            className={cn(
              'border-wtw-infinity-100 bg-wtw-infinity-50 focus-visible:ring-brand-400 rounded-[6px]',
              'border p-[10px] text-left focus-visible:ring-2 focus-visible:outline-hidden',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            <span className="text-steel-400 block text-[12px] leading-4">{draft.prompt}</span>
          </button>
        </div>

        <Button
          type="button"
          variant="transparent"
          aria-label="Cancel schedule"
          disabled={disabled}
          loading={pendingAction === 'cancel'}
          onClick={onCancel}
          className="text-steel-300 size-6 shrink-0 p-0"
        >
          {pendingAction !== 'cancel' && <X className="size-5" />}
        </Button>
      </div>

      <div className="border-steel-75 flex flex-1 flex-col border-b px-[31px] pb-5">
        <NovaScheduleProposalFields
          draft={draft}
          disabled={disabled}
          accountLabel={accountLabel}
          showAccount={showAccount}
          firstRunLabel={firstRunLabel}
          firstRunLoading={firstRunLoading}
          firstRunUnavailable={firstRunUnavailable}
          onEditAccount={canEditAccount ? onEditAccount : undefined}
          onEditWhen={onEditWhen}
          onEditDelivery={onEditDelivery}
        />

        {error && (
          <p role="alert" className="text-fire-500 mt-4 text-[12px] leading-4 font-semibold">
            {error}
          </p>
        )}
      </div>

      <ToolHandoffCardFooter className="px-[27px] py-5">
        <Button
          type="button"
          variant="tertiary"
          className="h-auto px-0 py-0 text-[14px] leading-5"
          disabled={disabled}
          loading={pendingAction === 'cancel'}
          onClick={onCancel}
        >
          Cancel
        </Button>

        <Button
          type="button"
          variant={null}
          className={cn(
            'text-wtw-ultraviolet-850 hover:text-wtw-ultraviolet-850 h-9 gap-[10px] rounded-full px-4 py-2 text-[14px] leading-5',
            novaGradientClass.aiBackground,
          )}
          disabled={disabled || !firstRunAvailable}
          loading={pendingAction === 'confirm'}
          onClick={onConfirm}
        >
          {pendingAction !== 'confirm' && <ArrowRight className="size-[18px]" />}
          Continue
        </Button>
      </ToolHandoffCardFooter>
    </div>
  );
}

function ScheduleWhenPanel({
  draft,
  firstRunLabel,
  browserTimezone,
  disabled,
  onChange,
  onBack,
  onUpdate,
}: {
  draft: NovaScheduleProposalDraft;
  firstRunLabel?: string;
  browserTimezone: string;
  disabled: boolean;
  onChange: (draft: NovaScheduleProposalDraft) => void;
  onBack: () => void;
  onUpdate: () => void;
}) {
  return (
    <>
      <div className="flex flex-col gap-4 px-[30px] pt-5 pb-4">
        <h3 className="text-steel-500 text-[14px] leading-5 font-semibold">When</h3>
        <NovaScheduleWhenEditor draft={draft} timezone={browserTimezone} disabled={disabled} onChange={onChange} />
      </div>

      <div className="border-steel-75 flex items-start gap-2 border-b px-[30px] pb-5">
        <Clock3 aria-hidden="true" className="text-steel-400 mt-px size-4 shrink-0" />
        <p className="text-steel-400 text-[12px] leading-4">
          <span className="text-steel-500 font-semibold">{formatNovaScheduleRecurrence(draft.recurrence)}</span>
          {firstRunLabel ? <span>{` · First run ${firstRunLabel}`}</span> : null}
        </p>
      </div>

      <NovaScheduleEditorFooter disabled={disabled} onBack={onBack} onUpdate={onUpdate} />
    </>
  );
}

function ScheduleDeliveryPanel({
  draft,
  disabled,
  onChange,
  onBack,
  onUpdate,
}: {
  draft: NovaScheduleProposalDraft;
  disabled: boolean;
  onChange: (draft: NovaScheduleProposalDraft) => void;
  onBack: () => void;
  onUpdate: () => void;
}) {
  return (
    <>
      <div className="border-steel-75 flex flex-col gap-4 border-b px-[30px] pt-5 pb-5">
        <h3 className="text-steel-500 text-[14px] leading-5 font-semibold">Delivery</h3>
        <NovaScheduleDeliveryEditor
          value={draft.outputPolicy}
          disabled={disabled}
          onChange={(outputPolicy) => onChange({ ...draft, outputPolicy })}
        />
      </div>

      <NovaScheduleEditorFooter disabled={disabled} onBack={onBack} onUpdate={onUpdate} />
    </>
  );
}

function SchedulePromptPanel({
  draft,
  accountUuid,
  existingFiles,
  attachments,
  disabled,
  onChange,
  onExistingFilesChange,
  setAttachments,
  onBack,
  onUpdate,
}: {
  draft: NovaScheduleProposalDraft;
  accountUuid?: string;
  existingFiles: NovaPromptFile[];
  attachments: FileAttachment[];
  disabled: boolean;
  onChange: (draft: NovaScheduleProposalDraft) => void;
  onExistingFilesChange: (files: NovaPromptFile[]) => void;
  setAttachments: React.Dispatch<React.SetStateAction<FileAttachment[]>>;
  onBack: () => void;
  onUpdate: () => void;
}) {
  const hasUnsettledAttachments = hasUnsettledNovaPromptAttachments(attachments);

  return (
    <>
      <div className="flex flex-col gap-4 px-[30px] pt-5 pb-4">
        <h3 className="text-steel-500 text-[14px] leading-5 font-semibold">Schedule prompt</h3>
        <NovaPromptEditor
          value={draft.prompt}
          accountUuid={accountUuid}
          existingFiles={existingFiles}
          attachments={attachments}
          disabled={disabled}
          onChange={(prompt) => onChange({ ...draft, prompt })}
          onExistingFilesChange={onExistingFilesChange}
          setAttachments={setAttachments}
        />
      </div>

      <div className="border-steel-75 border-b" />
      <NovaScheduleEditorFooter disabled={disabled || hasUnsettledAttachments} onBack={onBack} onUpdate={onUpdate} />
    </>
  );
}

function ResolvedNovaScheduleProposalCard({
  proposal,
  output,
  browserTimezone,
  onManageSchedules,
}: ResolvedNovaScheduleProposalCardProps & { browserTimezone: string }) {
  if (output.action === 'cancel') {
    const canceledItem = proposal.recurrence.preset === 'ONCE' ? 'reminder' : 'schedule';

    return (
      <div className="flex items-start gap-3 p-4">
        <span className="bg-steel-50 text-steel-400 flex size-8 shrink-0 items-center justify-center rounded-full">
          <X className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <p className="text-steel-500 text-[14px] leading-5 font-semibold">{proposal.title}</p>
            <Badge variant="neutral" className="px-2 py-0.5 text-[11px]">
              Canceled
            </Badge>
          </div>
          <p className="text-steel-400 text-[12px] leading-4">{`No ${canceledItem} was created.`}</p>
        </div>
      </div>
    );
  }

  const { schedule } = output;
  const isReminder = schedule.recurrence.preset === 'ONCE';

  return (
    <div className="flex flex-col gap-4 p-[15px]">
      <div className="flex items-start gap-3">
        <span className="bg-wtw-success-25 text-wtw-success-450 flex size-8 shrink-0 items-center justify-center rounded-full">
          <Check className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-steel-500 text-[14px] leading-5 font-semibold">{schedule.title}</p>
            <Badge variant={schedule.status === 'ACTIVE' ? 'success' : 'neutral'} className="px-2 py-0.5 text-[11px]">
              {sentenceCase(schedule.status)}
            </Badge>
          </div>
          <p className="text-steel-400 mt-1 text-[14px] leading-5">{schedule.prompt}</p>
        </div>
      </div>

      <div className="border-steel-75 flex flex-col gap-3 border-t pt-4">
        <ScheduleSummaryRow label="When" value={formatNovaScheduleRecurrence(schedule.recurrence)} />
        <ScheduleSummaryRow
          label="Delivery"
          value={
            isReminder
              ? 'Current chat'
              : schedule.outputPolicy === 'APPEND_TO_CHAT'
                ? 'Same schedule chat'
                : 'Start a fresh chat each run'
          }
        />
        <ScheduleSummaryRow
          label={isReminder ? 'Reminds you' : 'Next run'}
          value={formatNovaScheduleDateTime(schedule.nextRunAt, browserTimezone)}
        />
        {onManageSchedules ? (
          <button
            type="button"
            onClick={onManageSchedules}
            className="text-brand-400 hover:text-brand-500 w-fit text-[12px] leading-4 font-semibold"
          >
            Manage in Scheduled settings
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ScheduleSummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[74px_minmax(0,1fr)] items-start gap-4">
      <span className="text-steel-500 text-[12px] leading-4 font-semibold">{label}</span>
      <span className="text-steel-500 min-w-0 text-[14px] leading-5">{value}</span>
    </div>
  );
}

function sentenceCase(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase();
}
